import router, { useRouter } from 'next/router'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import { useSWRConfig } from 'swr'
import {
	COOKIE_NAME_JWT,
	COOKIE_NAME_USER_GROUP,
	PAGE_PATH_CUSTOMER,
	PAGE_PATH_HOME,
	QUERY_PARAM_PREVIOUS_PATH,
	USER_GROUP_NOT_LOGGED_IN,
} from '../../constants'
import { useCustomerLogin } from '../../hooks/customer/useCustomerLogin'
import { useCustomerLoginSocial } from '../../hooks/customer/useCustomerLoginSocial'
import { useCustomerRegister } from '../../hooks/customer/useCustomerRegister'
import { useTrackingsActions } from '../../hooks/trackings/useTrackingsActions'
import { useCookiesByGroup } from '../../hooks/useCookiesByGroup'
import { useVary } from '../../hooks/useVary'
import { getNextTenDaysDate } from '../../utils/date'
import { jwtDecode } from '../../utils/jwt'
import { sanitizePreviousPath } from '../../utils/url'
import { useUserSourceActions } from './user-source'

const UserActionsContext = createContext()

const authCookiesAreAligned = (jwt, userGroup) => {
	if (!jwt && (userGroup === undefined || userGroup === null)) {
		// jwt and userGroup are both undefined or null
		return true
	}
	if (!!jwt && jwtDecode(jwt)?.aud?.includes('self') && userGroup !== USER_GROUP_NOT_LOGGED_IN) {
		// jwt logged and userGroup different from 0
		return true
	}
	if (!!jwt && !jwtDecode(jwt)?.aud?.includes('self') && userGroup === USER_GROUP_NOT_LOGGED_IN) {
		// jwt not logged and userGroup equal 0
		return true
	}
	return false
}

const UserActionsProvider = ({ children }) => {
	const { cache } = useSWRConfig()
	const { setSource } = useUserSourceActions()
	const { query } = useRouter()
	const previousPath = query[QUERY_PARAM_PREVIOUS_PATH]

	const {
		action: loginAction,
		loading: loginLoading,
		error: loginError,
		clearError: loginClearError,
	} = useCustomerLogin()
	const {
		action: loginSocialAction,
		loading: loginSocialLoading,
		error: loginSocialError,
		clearError: loginSocialClearError,
	} = useCustomerLoginSocial()
	const {
		action: registerAction,
		loading: registerLoading,
		error: registerError,
		clearError: registerClearError,
	} = useCustomerRegister()

	const [
		{ [COOKIE_NAME_USER_GROUP]: cookieUserGroupValue, [COOKIE_NAME_JWT]: cookieJwtValue },
		setCookiesByGroup,
		removeCookiesByGroup,
	] = useCookiesByGroup([COOKIE_NAME_JWT, COOKIE_NAME_USER_GROUP])

	const { login: trackingLogin, register: trackingRegister } = useTrackingsActions()

	const cookieUserGroup = cookieUserGroupValue && parseInt(cookieUserGroupValue)

	const { update: updateVary } = useVary()

	const login = useCallback(
		async ({ email, password, source, redirect = true, redirectTo = PAGE_PATH_CUSTOMER }) => {
			const response = await loginAction({ email, password, source })
			if (response) {
				const { token, sessionData: { groupId } = {} } = response

				if (token && groupId !== undefined) {
					trackingLogin()
					setCookiesByGroup(COOKIE_NAME_JWT, token, { expires: getNextTenDaysDate() })
					setCookiesByGroup(COOKIE_NAME_USER_GROUP, groupId, { expires: getNextTenDaysDate() })
					setSource(source)
					updateVary()
					cache.clear() // clear swr cache

					if (redirect) {
						router.push(sanitizePreviousPath(previousPath) ?? redirectTo)
					}
				}
				return response
			} else {
				return false
			}
		},
		[loginAction, trackingLogin, cache, setCookiesByGroup, setSource, previousPath, updateVary]
	)

	const loginSocial = useCallback(
		async ({ socialType, socialResponse, source, redirect = true, redirectTo = PAGE_PATH_CUSTOMER }) => {
			const response = await loginSocialAction({ socialType, socialResponse, source })
			if (response) {
				const { token, sessionData: { groupId } = {} } = response
				if (token && groupId !== undefined) {
					trackingLogin()
					setCookiesByGroup(COOKIE_NAME_JWT, token, { expires: getNextTenDaysDate() })
					setCookiesByGroup(COOKIE_NAME_USER_GROUP, groupId, { expires: getNextTenDaysDate() })
					setSource(source)
					updateVary()
					cache.clear() // clear swr cache

					if (redirect) {
						router.push(sanitizePreviousPath(previousPath) ?? redirectTo)
					}
				}
				return response
			} else {
				return false
			}
		},
		[loginSocialAction, setCookiesByGroup, cache, setSource, trackingLogin, previousPath, updateVary]
	)

	const register = useCallback(
		async ({ formData, redirect = true, redirectTo = PAGE_PATH_CUSTOMER }) => {
			const response = await registerAction(formData)
			if (response) {
				trackingRegister()
				await login({ email: formData.email, password: formData.password, redirect, redirectTo })
				return response
			} else {
				return false
			}
		},
		[registerAction, trackingRegister, login]
	)

	const logout = useCallback(
		({ redirect = false, redirectTo = PAGE_PATH_HOME } = {}) => {
			removeCookiesByGroup(COOKIE_NAME_JWT)
			removeCookiesByGroup(COOKIE_NAME_USER_GROUP)
			updateVary()
			cache.clear() // clear swr cache

			if (redirect) {
				router.push(redirectTo)
			}
		},
		[removeCookiesByGroup, cache, updateVary]
	)

	useEffect(() => {
		// Logout if auth cookies aren't aligned
		if (!authCookiesAreAligned(cookieJwtValue, cookieUserGroup)) {
			logout()
		}
	}, [logout, cookieJwtValue, cookieUserGroup])

	const userActions = useMemo(
		() => ({
			login,
			loginSocial,
			logout,
			loginLoading,
			loginSocialLoading,
			loginError,
			loginSocialError,
			loginClearError,
			loginSocialClearError,
			register,
			registerLoading,
			registerError,
			registerClearError,
		}),
		[
			login,
			loginClearError,
			loginError,
			loginLoading,
			loginSocial,
			loginSocialClearError,
			loginSocialError,
			loginSocialLoading,
			logout,
			register,
			registerClearError,
			registerError,
			registerLoading,
		]
	)

	return <UserActionsContext.Provider value={userActions}>{children}</UserActionsContext.Provider>
}

export default UserActionsProvider
export const useUserActions = () => useContext(UserActionsContext)
